import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Loader2, Wifi, PhoneCall } from "lucide-react";
import { useData } from "@/context/DataContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/common";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { uploadFile } from "@/lib/upload";
import { euro } from "@/lib/format";
import { EVENT_ACCESS_OPTIONS } from "@/lib/eventConstants";
import client from "@/lib/api";
import { toast } from "sonner";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const TYPES = ["Conférence", "Séminaire", "Congrès", "Team Building", "Convention stratégique", "Gala", "Déjeuner d'affaire", "Dîner d'affaire", "Inauguration", "Journée portes ouvertes", "Salon", "Formation", "Colloque", "Foire", "Voyage d'entreprise", "Départ à la retraite", "Autre"];
const FORMATS = ["Présentiel", "Distanciel", "Hybride"];
const VISIBILITY = ["Public", "Privé", "Sur invitation"];

function Field({ label, children, className = "" }) {
  return (<div className={className}><Label className="mb-1.5 block">{label}</Label>{children}</div>);
}

export default function CreateEvent() {
  const { addEvent } = useData();
  const navigate = useNavigate();
  const [f, setF] = useState({
    name: "", type: "", description: "", objective: "", language: "Français",
    startDate: "", endDate: "", city: "", address: "", banner: "", maxParticipants: "", budget: "", contact: "", contactEmail: "", color: "#8b5cf6",
    accessParking: "", accessTransport: "", accessTransportMode: "", wifiNetwork: "", wifiPassword: "", wifiInstructions: "", emergencyNumber: "", emergencyNote: "",
    venuePlanUrl: "", participantGuideUrl: "",
  });
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [venuePlanUploading, setVenuePlanUploading] = useState(false);
  const [guideUploading, setGuideUploading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const onUploadPdf = async (ev, key, setUploading) => {
    const file = ev.target.files?.[0]; if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Fichier trop lourd (max 15 Mo)"); ev.target.value = ""; return; }
    setUploading(true);
    try { const url = await uploadFile(file); set(key, url); toast.success("Fichier PDF ajouté"); }
    catch { toast.error("Upload impossible"); }
    finally { setUploading(false); ev.target.value = ""; }
  };

  const suggestAccess = async () => {
    if (!f.address && !f.city) { toast.error("Renseignez d'abord l'adresse ou la ville"); return; }
    setAiLoading(true);
    try {
      const { data } = await client.post("/ai/venue-access", { address: f.address, city: f.city });
      setF((p) => ({ ...p, accessParking: data.parking || p.accessParking, accessTransport: data.transports || data.publicTransport || p.accessTransport }));
      toast.success("Suggestions générées — vérifiez et ajustez si besoin");
    } catch { toast.error("Suggestion IA indisponible"); }
    finally { setAiLoading(false); }
  };

  const perParticipant = f.budget && f.maxParticipants ? Math.round(Number(f.budget) / Number(f.maxParticipants)) : 0;

  const onBanner = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { const url = await uploadFile(file); set("banner", url); toast.success("Bannière ajoutée"); }
    catch { toast.error("Échec de l'upload"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (f.endDate && f.startDate && f.endDate < f.startDate) { toast.error("La date de fin doit être ≥ à la date de début"); return; }
    if (f.contactEmail && !EMAIL_RE.test(f.contactEmail.trim())) { toast.error("L'email du contact n'est pas valide"); return; }
    setSaving(true);
    try {
      const slug = (f.name || "event").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40) || "event";
      const registrationLink = `${window.location.origin}/inscription/${slug}-${Math.random().toString(36).slice(2, 8)}`;
      await addEvent({ ...f, registrationLink, budget: Number(f.budget) || 0, maxParticipants: Number(f.maxParticipants) || 0 });
      toast.success("Événement créé");
      navigate("/events");
    } catch { toast.error("Erreur lors de la création"); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader title="Créer un événement" subtitle="Renseignez les informations principales de votre événement" />
      <form onSubmit={submit} className="space-y-6">
        <div className="bg-card rounded-xl border border-border p-6">
          <h3 className="font-display font-semibold mb-4">Informations générales</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nom de l'événement" className="sm:col-span-2"><Input required data-testid="event-name" value={f.name} onChange={(e) => set("name", e.target.value)} /></Field>
            <Field label="Type">
              <Select value={f.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger data-testid="event-type"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Objectif"><Input data-testid="event-objective" value={f.objective} onChange={(e) => set("objective", e.target.value)} /></Field>
            <Field label="Description" className="sm:col-span-2"><Textarea rows={3} data-testid="event-description" placeholder="Décrivez votre événement..." value={f.description} onChange={(e) => set("description", e.target.value)} /></Field>
          </div>
        </div>

        <div className="bg-card rounded-xl border border-border p-6">
          <h3 className="font-display font-semibold mb-4">Dates & lieu</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Date de début"><Input type="date" required data-testid="event-start" value={f.startDate} onChange={(e) => set("startDate", e.target.value)} /></Field>
            <Field label="Date de fin"><Input type="date" data-testid="event-end" value={f.endDate} min={f.startDate} onChange={(e) => set("endDate", e.target.value)} /></Field>
            <Field label="Ville"><Input data-testid="event-city" value={f.city} onChange={(e) => set("city", e.target.value)} /></Field>
            <Field label="Adresse (vérifiée par Google Maps)" className="sm:col-span-2">
              <AddressAutocomplete testid="event-address" value={f.address} city={f.city}
                onChange={(v) => set("address", v)}
                onResolved={(r) => setF((p) => ({ ...p, address: r.formatted, city: r.city || p.city }))} />
            </Field>
            <Field label="Bannière (image)" className="sm:col-span-2">
              <Input type="file" accept="image/*" data-testid="event-banner" onChange={onBanner} />
              {f.banner && <img src={f.banner} alt="" className="mt-2 h-24 rounded-lg object-cover" />}
            </Field>
          </div>
        </div>

        <div className="bg-card rounded-xl border border-border p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h3 className="font-display font-semibold">Accès sur site</h3>
            <Button type="button" variant="outline" size="sm" onClick={suggestAccess} disabled={aiLoading} data-testid="suggest-access">
              {aiLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}Proposer avec l'IA
            </Button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Parking le plus proche"><Input data-testid="event-parking" value={f.accessParking} onChange={(e) => set("accessParking", e.target.value)} placeholder="Ex. Parking Indigo, 2 min à pied" /></Field>
            <Field label="Mode d'accès recommandé">
              <Select value={f.accessTransportMode} onValueChange={(v) => set("accessTransportMode", v)}>
                <SelectTrigger data-testid="event-transport-mode"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>{EVENT_ACCESS_OPTIONS.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Transports et consignes" className="sm:col-span-2"><Input data-testid="event-transport" value={f.accessTransport} onChange={(e) => set("accessTransport", e.target.value)} placeholder="Ex. Navette depuis la gare, métro L1 station Bastille" /></Field>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">Suggestions IA à valider ou à saisir directement.</p>
        </div>

        <div className="bg-card rounded-xl border border-border p-6">
          <h3 className="font-display font-semibold mb-4">Infos pratiques</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Réseau Wifi (nom)"><div className="relative"><Wifi className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input className="pl-9" data-testid="event-wifi-network" value={f.wifiNetwork} onChange={(e) => set("wifiNetwork", e.target.value)} placeholder="JustPlanIt-Guest" /></div></Field>
            <Field label="Mot de passe Wifi"><Input data-testid="event-wifi-password" value={f.wifiPassword} onChange={(e) => set("wifiPassword", e.target.value)} /></Field>
            <Field label="Instructions Wifi (optionnel)" className="sm:col-span-2"><Input data-testid="event-wifi-instructions" value={f.wifiInstructions || ""} onChange={(e) => set("wifiInstructions", e.target.value)} placeholder="Ex. Connectez-vous puis acceptez les CGU sur la page de portail" /></Field>
            <Field label="Numéro d'urgence sur site" className="sm:col-span-2"><div className="relative"><PhoneCall className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input className="pl-9" data-testid="event-emergency" value={f.emergencyNumber} onChange={(e) => set("emergencyNumber", e.target.value)} placeholder="Ex. +33 6 12 34 56 78" /></div></Field>
            <Field label="Note explicative (numéro d'urgence)" className="sm:col-span-2"><Input data-testid="event-emergency-note" value={f.emergencyNote || ""} onChange={(e) => set("emergencyNote", e.target.value)} placeholder="Ex. Régie technique · 24h/24 pendant l'événement" /></Field>
            <Field label="📍 Plan du lieu (PDF)" className="sm:col-span-1">
              <div className="flex items-center gap-2">
                <input type="file" accept="application/pdf" onChange={(e) => onUploadPdf(e, "venuePlanUrl", setVenuePlanUploading)} className="hidden" id="venuePlan" data-testid="event-venue-plan-input" />
                <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById("venuePlan").click()} disabled={venuePlanUploading} data-testid="event-venue-plan-btn">{venuePlanUploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}{f.venuePlanUrl ? "Remplacer" : "Ajouter"} le plan</Button>
                {f.venuePlanUrl && <a href={f.venuePlanUrl} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 underline">Voir</a>}
              </div>
            </Field>
            <Field label="📕 Guide du participant (PDF)" className="sm:col-span-1">
              <div className="flex items-center gap-2">
                <input type="file" accept="application/pdf" onChange={(e) => onUploadPdf(e, "participantGuideUrl", setGuideUploading)} className="hidden" id="participantGuide" data-testid="event-guide-input" />
                <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById("participantGuide").click()} disabled={guideUploading} data-testid="event-guide-btn">{guideUploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}{f.participantGuideUrl ? "Remplacer" : "Ajouter"} le guide</Button>
                {f.participantGuideUrl && <a href={f.participantGuideUrl} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 underline">Voir</a>}
              </div>
            </Field>
          </div>
        </div>

        <div className="bg-card rounded-xl border border-border p-6">
          <h3 className="font-display font-semibold mb-4">Participants & budget</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Participants max"><Input type="number" data-testid="event-participants" value={f.maxParticipants} onChange={(e) => set("maxParticipants", e.target.value)} /></Field>
            <Field label="Budget total (€)"><Input type="number" data-testid="event-budget" value={f.budget} onChange={(e) => set("budget", e.target.value)} /></Field>
            <Field label="Budget / participant"><Input disabled value={euro(perParticipant)} data-testid="event-budget-per" /></Field>
          </div>
        </div>

        <div className="bg-card rounded-xl border border-border p-6">
          <h3 className="font-display font-semibold mb-4">Contact & couleur</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Contact principal"><Input data-testid="event-contact" value={f.contact} onChange={(e) => set("contact", e.target.value)} /></Field>
            <Field label="Email du contact principal"><Input type="email" data-testid="event-contact-email" placeholder="contact@entreprise.fr" value={f.contactEmail} onChange={(e) => set("contactEmail", e.target.value)} /></Field>
            <Field label="Couleur de l'événement">
              <div className="flex items-center gap-3">
                <input type="color" value={f.color} onChange={(e) => set("color", e.target.value)} data-testid="event-color" className="h-10 w-16 rounded-lg border border-border cursor-pointer" />
                <span className="font-mono text-sm">{f.color}</span>
              </div>
            </Field>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate("/dashboard")}>Annuler</Button>
          <Button type="submit" disabled={saving} data-testid="submit-event">Créer l'événement</Button>
        </div>
      </form>
    </div>
  );
}
